import { type DependencyList, useCallback, useEffect, useRef } from 'react';

const DEFAULT_BOTTOM_THRESHOLD = 48;

interface UseStickyChatScrollOptions {
  watch: DependencyList;
  bottomThreshold?: number;
}

export const useStickyChatScroll = <T extends HTMLElement>({
  watch,
  bottomThreshold = DEFAULT_BOTTOM_THRESHOLD,
}: UseStickyChatScrollOptions) => {
  const scrollRef = useRef<T>(null);
  const stickToBottomRef = useRef(true);
  const ignoreProgrammaticScrollRef = useRef(false);

  const isNearBottom = useCallback(
    (element: T) => (
      element.scrollHeight - element.scrollTop - element.clientHeight <= bottomThreshold
    ),
    [bottomThreshold]
  );

  const scrollToBottom = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    ignoreProgrammaticScrollRef.current = true;
    element.scrollTop = element.scrollHeight;

    window.requestAnimationFrame(() => {
      ignoreProgrammaticScrollRef.current = false;
      const currentElement = scrollRef.current;
      if (currentElement) {
        stickToBottomRef.current = isNearBottom(currentElement);
      }
    });
  }, [isNearBottom]);

  const resumeAutoScroll = useCallback(() => {
    stickToBottomRef.current = true;
    scrollToBottom();
  }, [scrollToBottom]);

  const handleScroll = useCallback(() => {
    const element = scrollRef.current;
    if (!element || ignoreProgrammaticScrollRef.current) return;
    stickToBottomRef.current = isNearBottom(element);
  }, [isNearBottom]);

  useEffect(() => {
    if (!stickToBottomRef.current) return;
    scrollToBottom();
  }, watch);

  return {
    scrollRef,
    handleScroll,
    resumeAutoScroll,
  };
};
